import { t } from "../i18n";
import { Check, Minus, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { DraftLine, Product } from "../types";
import { formatMoney, splitVariations, variationMultiplier } from "../utils";
import { useBackHandler } from "../backNavigation";

type Props = {
  product: Product;
  draftLines: DraftLine[];
  onCancel: () => void;
  onSave: (quantities: Record<string, number>) => void;
};

function parseCount(value: string): number {
  const parsed = Number.parseInt(value.replace(/\D/g, ""), 10);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(99, parsed)) : 0;
}

export function VariationsDialog({ product, draftLines, onCancel, onSave }: Props) {
  useBackHandler(true, onCancel);
  const variations = useMemo(() => splitVariations(product.variation_options), [product.variation_options]);
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      variations.map((variation) => [
        variation,
        draftLines.find((line) => line.productId === product.id && line.variation === variation)?.quantity ?? 0
      ])
    )
  );

  function update(variation: string, value: number) {
    setQuantities((current) => ({
      ...current,
      [variation]: Math.max(0, Math.min(99, value))
    }));
  }

  return createPortal(
    <div
      className="dialog-backdrop"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) {
          onCancel();
        }
      }}
    >
      <section className="variation-dialog" role="dialog" aria-modal="true" aria-labelledby="variation-title">
        <header className="dialog-header">
          <div>
            <h2 id="variation-title">{product.name}</h2>
            <p>{t("Επιλογή ποσότητας")}</p>
          </div>
          <button type="button" className="icon-button" onClick={onCancel} aria-label={t("Κλείσιμο")}>
            <X />
          </button>
        </header>
        <div className="variation-list">
          {variations.map((variation) => {
            const value = quantities[variation] ?? 0;
            return (
              <div className="variation-row" key={variation}>
                <button
                  type="button"
                  className="variation-label"
                  onClick={() => update(variation, value + 1)}
                >
                  <strong>{variation}</strong>
                  <small>{formatMoney(product.price * variationMultiplier(variation))}</small>
                </button>
                <span className="counter-controls">
                  <button type="button" className="icon-button" onClick={() => update(variation, value - 1)}>
                    <Minus />
                  </button>
                  <input
                    inputMode="numeric"
                    value={value}
                    onFocus={(event) => event.currentTarget.select()}
                    onChange={(event) => update(variation, parseCount(event.target.value))}
                    aria-label={t("Ποσότητα {0}", variation)}
                  />
                  <button type="button" className="icon-button" onClick={() => update(variation, value + 1)}>
                    <Plus />
                  </button>
                </span>
              </div>
            );
          })}
        </div>
        <footer className="dialog-actions">
          <button type="button" className="secondary-command" onClick={onCancel}>
            <X />
            <span>{t("Ακύρωση")}</span>
          </button>
          <button type="button" className="primary-command" onClick={() => onSave(quantities)}>
            <Check />
            <span>{t("Εφαρμογή")}</span>
          </button>
        </footer>
      </section>
    </div>,
    document.body
  );
}
